// AI Maturity tab — SGNL-90.
//
// Distribution histogram of AI Maturity stages across the filtered set,
// split into rep-set vs LLM-suggested counts so operators can see at a
// glance "where do reps say we are" vs "where does the model say we
// are". Click a histogram bucket to drill into Deal Explorer with that
// rep-set ai_maturity pre-applied.
//
// A second section lists opps that have ONLY a suggestion (no rep-set
// maturity) — these are the "needs attention" rows from R5's design
// where SGNL-29's badge surfaces and the Apply button (locked behind
// R6) will eventually push back to Salesforce.
//
// Pure-vanilla DOM, no framework, every visual element reuses existing
// CSS classes (.tab-content, .data-table.sortable, .table-wrap, .chip,
// .row-count, .muted, .empty-bucket). Zero new classes.

import { esc, fmtMoney } from "../lib/format";
import { chipClassForConfidence } from "../lib/maturity";
import { setActiveTab, setFilters } from "../state";
import type { OpportunityRow } from "../types";

// Five canonical stage labels — match the rep-set values that show up
// in opp.ai_maturity from Salesforce. Suggestions return integer 1-5
// which we map onto the same buckets for the histogram. Anything
// outside the five (legacy values) buckets to "Other"; null buckets to
// "Unset".
// SGNL-96 — Bounteous rubric (Enhance/Extend/Transform/Reinvent/Revolutionize)
// inferred from the production rep-set values. The prior generic AI-maturity
// rubric (Aware/Experimenting/Operationalising/Industrialising/Transforming)
// was customer-readiness framing; the Bounteous rubric measures deal-level
// AI ambition (how AI-driven is THIS engagement). Histogram buckets keep
// stage-number prefix matching so legacy short-form "Stage 3" still buckets
// correctly during the v1→v2 rerun window.
const STAGE_LABELS = [
  "Stage 1: Enhance",
  "Stage 2: Extend",
  "Stage 3: Transform",
  "Stage 4: Reinvent",
  "Stage 5: Revolutionize",
] as const;

interface BucketRow {
  label: string;
  rep_count: number;
  suggested_count: number;
  total_amount: number;
  // Filter value to apply on drill-through. null for the "Unset" bucket;
  // we set hasMaturitySuggestion=true there so the drill shows opps
  // with suggestions but no rep-set value.
  filter_value: string | null;
  // For "Suggested only" bucket, drill on hasMaturitySuggestion=true.
  is_suggested_only?: boolean;
  // SGNL-103 — only populated on the "Unset" bucket. Per-stage
  // distribution of LLM suggestions for Unset opps. Pre-fix the
  // Suggested # column on Unset always showed 0 (suggestions land
  // in their stage bucket, not the Unset row), reading as if no
  // suggestions existed when actually they all distributed across
  // Stage 1-5. UI renders this as a mini-chip group in the Suggested
  // # cell so operators see at-a-glance that LLM has classified the
  // unset opps.
  unset_suggestion_distribution?: Record<string, number>;
}

function bucketKeyForRep(opp: OpportunityRow): string {
  if (!opp.ai_maturity) return "Unset";
  // Match by stage prefix + boundary so "Stage 10:..." doesn't match
  // "Stage 1" (real failure mode flagged by code-reviewer agent on PR
  // #40 — legacy SF picklists may carry stage values >5). The boundary
  // can be ":" (canonical "Stage 3: Transform") or end-of-string
  // (legacy short form "Stage 3").
  for (const lbl of STAGE_LABELS) {
    const stageNum = lbl.split(":")[0]; // "Stage 1"
    if (
      opp.ai_maturity === stageNum ||
      opp.ai_maturity.startsWith(`${stageNum}:`) ||
      opp.ai_maturity.startsWith(`${stageNum} `)
    ) {
      return lbl;
    }
  }
  return "Other";
}

function renderUnsetDistribution(dist: Record<string, number>): string {
  // SGNL-103 — render per-stage suggestion counts as a mini chip group
  // for the Unset row. Stages in canonical order, omitting any with
  // zero suggestions to keep the row compact. Each chip carries a
  // `data-stage-suggestion` attribute the row's click handler reads
  // for drill-through (separate from the row's own filter, which is
  // null for Unset). Falls back to "—" if every stage has zero.
  const parts: string[] = [];
  for (const lbl of STAGE_LABELS) {
    const n = dist[lbl] ?? 0;
    if (n === 0) continue;
    const stagePrefix = lbl.split(":")[0]; // "Stage 1"
    parts.push(
      `<span class="chip blue" style="margin-right:4px; font-size:11px;" ` +
        `data-stage-suggestion="${esc(stagePrefix)}" ` +
        `title="${esc(`${n} unset opp(s) suggested as ${lbl}`)}">` +
        `${esc(stagePrefix)} ×${n.toLocaleString()}</span>`,
    );
  }
  return parts.length > 0
    ? `<span data-testid="unset-suggestion-distribution">${parts.join("")}</span>`
    : `<span class="muted">—</span>`;
}


function bucketKeyForSuggested(opp: OpportunityRow): string | null {
  const s = opp.maturity_suggestion;
  if (!s) return null;
  const idx = s.suggested_stage - 1;
  if (idx >= 0 && idx < STAGE_LABELS.length) return STAGE_LABELS[idx];
  return null;
}

function computeBuckets(rows: OpportunityRow[]): BucketRow[] {
  const map = new Map<string, BucketRow>();
  const ensure = (label: string, filter_value: string | null): BucketRow => {
    let b = map.get(label);
    if (!b) {
      b = {
        label,
        rep_count: 0,
        suggested_count: 0,
        total_amount: 0,
        filter_value,
      };
      map.set(label, b);
    }
    return b;
  };

  for (const r of rows) {
    const repBucket = bucketKeyForRep(r);
    const repFilter = repBucket === "Unset" || repBucket === "Other" ? null : repBucket;
    const repB = ensure(repBucket, repFilter);
    repB.rep_count += 1;
    repB.total_amount += r.amount ?? 0;

    const sugBucket = bucketKeyForSuggested(r);
    if (sugBucket) {
      const sugB = ensure(sugBucket, sugBucket);
      sugB.suggested_count += 1;
      // SGNL-103 — when an Unset opp has an LLM suggestion, also
      // tally the suggestion's stage on the Unset bucket itself so
      // its row can render the per-stage distribution instead of a
      // misleading 0.
      if (repBucket === "Unset") {
        if (!repB.unset_suggestion_distribution) {
          repB.unset_suggestion_distribution = {};
        }
        repB.unset_suggestion_distribution[sugBucket] =
          (repB.unset_suggestion_distribution[sugBucket] ?? 0) + 1;
      }
    }
  }

  // Order: 1 → 5 in canonical sequence, then Unset, then Other (if any).
  const order = [...STAGE_LABELS, "Unset", "Other"] as const;
  return order
    .map((l) => map.get(l))
    .filter((b): b is BucketRow => b !== undefined);
}

export function renderMaturityTab(host: HTMLElement, rows: OpportunityRow[]): void {
  const buckets = computeBuckets(rows);
  const totalSuggestions = rows.filter((r) => r.maturity_suggestion).length;
  const noRepWithSuggestion = rows.filter(
    (r) => !r.ai_maturity && r.maturity_suggestion,
  );
  const total = rows.length;

  if (total === 0) {
    host.innerHTML = `
      <div class="tab-content" data-testid="maturity-tab">
        <div class="tab-header"><h2>AI Maturity</h2></div>
        <p class="empty-bucket">No opportunities in the current filter.</p>
      </div>`;
    return;
  }

  // Histogram bar uses the existing chip palette so a Stage-3 bucket
  // here looks consistent with a Stage-3 badge in Deal Explorer.
  const maxRep = Math.max(1, ...buckets.map((b) => b.rep_count));
  const histRows = buckets
    .map((b) => {
      const pct = (b.rep_count / maxRep) * 100;
      const filterAttr =
        b.filter_value !== null ? `data-filter-value="${esc(b.filter_value)}"` : "";
      // SGNL-103 — Unset row replaces the misleading "Suggested # = 0"
      // with mini-chips showing where the LLM placed those opps. Other
      // rows keep the simple count.
      const suggestedCell =
        b.label === "Unset" && b.unset_suggestion_distribution
          ? renderUnsetDistribution(b.unset_suggestion_distribution)
          : `<span class="num">${b.suggested_count.toLocaleString()}</span>`;
      return `
        <tr data-bucket="${esc(b.label)}" ${filterAttr}
            data-testid="maturity-bucket-row" style="cursor:pointer;">
          <td>${esc(b.label)}</td>
          <td class="num">${b.rep_count.toLocaleString()}</td>
          <td>
            <div style="display:inline-block; width:${pct.toFixed(1)}%;
                        max-width:200px; min-width:1px;
                        background:var(--purple-rain); height:10px;
                        border-radius:2px; vertical-align:middle;"></div>
          </td>
          <td>${suggestedCell}</td>
          <td class="num">${fmtMoney(b.total_amount)}</td>
        </tr>`;
    })
    .join("");

  // Second section: opps where we have a suggestion but no rep-set
  // maturity. These are the actionable rows for the eventual Apply
  // workflow.
  const noRepRows = noRepWithSuggestion
    .slice(0, 100)
    .map((r) => {
      const s = r.maturity_suggestion!;
      const conf = (Number(s.confidence) * 100).toFixed(0);
      const chipCls = chipClassForConfidence(s.confidence);
      // SGNL-107 (CR-Q4 follow-up on PR #54): same XSS-hardening as the
      // shared `renderMaturityBadge` — the inline chip here was missed
      // in the original SGNL-107 scope. Number()-coerce + esc() so a
      // tampered API payload can't break the chip's class attribute or
      // inject HTML via the stage / confidence text.
      const stageNum = Number(s.suggested_stage);
      const stageText = Number.isNaN(stageNum) ? "?" : esc(String(stageNum));
      const confText = esc(conf);
      return `
        <tr data-testid="maturity-suggested-row">
          <td><a class="drill" href="#opp/${esc(r.id)}">${esc(r.name)}</a></td>
          <td>${esc(r.account_name ?? "—")}</td>
          <td>${esc(r.stage ?? "—")}</td>
          <td class="num">${fmtMoney(r.amount)}</td>
          <td>
            <span class="${chipCls}">Stage ${stageText} (${confText}%)</span>
          </td>
        </tr>`;
    })
    .join("");

  host.innerHTML = `
    <div class="tab-content" data-testid="maturity-tab">
      <div class="tab-header">
        <h2>AI Maturity <span class="row-count">${total.toLocaleString()} opps · ${totalSuggestions.toLocaleString()} with LLM suggestion</span></h2>
        <span class="muted">click a bucket to drill into Deal Explorer</span>
      </div>
      <div class="table-wrap">
        <table class="data-table sortable" data-testid="maturity-histogram">
          <thead>
            <tr>
              <th>Stage</th>
              <th class="num">Rep-set #</th>
              <th>Distribution</th>
              <th class="num">Suggested #</th>
              <th class="num">Total $</th>
            </tr>
          </thead>
          <tbody>${histRows}</tbody>
        </table>
      </div>
    </div>
    <div class="tab-content" data-testid="maturity-needs-attention" style="margin-top:18px;">
      <div class="tab-header">
        <h2>Needs attention <span class="row-count">${noRepWithSuggestion.length.toLocaleString()} opps with suggestion + no rep stage</span></h2>
      </div>
      ${
        noRepWithSuggestion.length === 0
          ? '<p class="empty-bucket">No suggestion-only opps in the current filter.</p>'
          : `<div class="table-wrap">
               <table class="data-table sortable">
                 <thead>
                   <tr>
                     <th>Opportunity</th>
                     <th>Account</th>
                     <th>Stage</th>
                     <th class="num">Amount</th>
                     <th>Suggestion</th>
                   </tr>
                 </thead>
                 <tbody>${noRepRows}</tbody>
               </table>
             </div>
             ${
               noRepWithSuggestion.length > 100
                 ? `<p class="table-footnote">Showing first 100 of ${noRepWithSuggestion.length.toLocaleString()} rows.</p>`
                 : ""
             }`
      }
    </div>`;

  // Drill: click a histogram bucket → set ai_maturity filter (or
  // hasMaturitySuggestion for the Unset bucket) and switch to deals.
  for (const tr of host.querySelectorAll<HTMLTableRowElement>(
    '[data-testid="maturity-bucket-row"]',
  )) {
    tr.addEventListener("click", () => {
      const bucket = tr.dataset.bucket;
      const filterValue = tr.dataset.filterValue;
      if (!bucket) return;
      if (bucket === "Unset") {
        // Drill into "needs attention" — opps without rep maturity but
        // with a suggestion. hasMaturitySuggestion=true handles that.
        setFilters({ hasMaturitySuggestion: true, aiMaturity: undefined });
      } else if (filterValue) {
        // SGNL-90 — drill on the canonical stage prefix. filter_value
        // is e.g. "Stage 3: Transform"; we send just "Stage 3"
        // (the stageNum prefix from STAGE_LABELS) so the backend +
        // applyFilters prefix-match catches both canonical and legacy
        // short-form values.
        const stageNum = filterValue.split(":")[0];
        setFilters({ aiMaturity: stageNum, hasMaturitySuggestion: undefined });
      }
      setActiveTab("deals");
    });
  }
}
